import { useState } from 'react';
import { Button, Grid, Group, NumberInput, Paper, Stack, Switch, TextInput, Textarea, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { ErrorAlert } from '@/components/error-alert';
import { TemplateVariableEditor } from '@/components/template-variable-editor';
import type { TemplateVariable } from '@/components/template-variable-editor';
import { OutputHandleEditor } from '@/components/output-handles';
import type { OutputHandle } from '@/components/output-handles';

export type PersonaTemplateValues = {
  name: string;
  description: string;
  category: string;
  featured: boolean;
  sortOrder: number;
  variables: TemplateVariable[];
  personaName: string;
  personaDescription: string;
  instructions: string;
  greeting: string;
  maxTurns: number;
  outputHandles: OutputHandle[];
};

export const emptyPersonaTemplateValues: PersonaTemplateValues = {
  name: '',
  description: '',
  category: '',
  featured: false,
  sortOrder: 0,
  variables: [],
  personaName: '',
  personaDescription: '',
  instructions: '',
  greeting: '',
  maxTurns: 10,
  outputHandles: [],
};

type PersonaTemplateEditorProps = {
  initialValues: PersonaTemplateValues;
  submitLabel: string;
  onSubmit: (values: PersonaTemplateValues) => Promise<string | null>;
};

export function PersonaTemplateEditor({ initialValues, submitLabel, onSubmit }: PersonaTemplateEditorProps) {
  const navigate = useNavigate();

  const [values, setValues] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (updates: Partial<PersonaTemplateValues>) => setValues((prev) => ({ ...prev, ...updates }));

  const goBack = () => void navigate({ to: '/system/persona-templates' });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    const message = await onSubmit({
      ...values,
      name: values.name.trim(),
      description: values.description.trim(),
      category: values.category.trim(),
      personaName: values.personaName.trim(),
      personaDescription: values.personaDescription.trim(),
    });

    setIsSubmitting(false);

    if (message) {
      setError(message);

      return;
    }

    goBack();
  };

  return (
    <Stack gap="lg">
      <Group>
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={goBack}>
          Back
        </Button>
      </Group>

      <ErrorAlert message={error} />

      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Title order={4}>Template Metadata</Title>
              <TextInput
                label="Template Name"
                placeholder="e.g. Customer Support Agent"
                value={values.name}
                onChange={(e) => update({ name: e.target.value })}
                required
              />
              <Textarea
                label="Template Description"
                placeholder="Describe what this template is for"
                value={values.description}
                onChange={(e) => update({ description: e.target.value })}
                required
              />
              <Group grow>
                <TextInput
                  label="Category"
                  placeholder="e.g. Support, Sales"
                  value={values.category}
                  onChange={(e) => update({ category: e.target.value })}
                />
                <NumberInput
                  label="Sort Order"
                  value={values.sortOrder}
                  onChange={(value) => update({ sortOrder: typeof value === 'number' ? value : 0 })}
                />
              </Group>
              <Switch
                label="Featured"
                checked={values.featured}
                onChange={(e) => update({ featured: e.currentTarget.checked })}
              />
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Title order={4}>Persona Content</Title>
              <TextInput
                label="Persona Name"
                description="Use <<varKey>> for variable placeholders"
                placeholder="e.g. <<companyName>> Support Agent"
                value={values.personaName}
                onChange={(e) => update({ personaName: e.target.value })}
                required
              />
              <Textarea
                label="Persona Description"
                placeholder="e.g. AI agent for <<companyName>>"
                value={values.personaDescription}
                onChange={(e) => update({ personaDescription: e.target.value })}
              />
              <Textarea
                label="Instructions"
                placeholder="System instructions with <<variables>>"
                value={values.instructions}
                onChange={(e) => update({ instructions: e.target.value })}
                autosize
                minRows={4}
              />
              <Textarea
                label="Greeting"
                placeholder="e.g. Hello, welcome to <<companyName>>!"
                value={values.greeting}
                onChange={(e) => update({ greeting: e.target.value })}
                autosize
                minRows={2}
              />
              <NumberInput
                label="Max Turns"
                value={values.maxTurns}
                onChange={(value) => update({ maxTurns: typeof value === 'number' ? value : 10 })}
                min={1}
                max={100}
              />
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      <Paper p="lg" withBorder>
        <Stack gap="md">
          <Title order={4}>Variables</Title>
          <TemplateVariableEditor variables={values.variables} onChange={(variables) => update({ variables })} />
        </Stack>
      </Paper>

      <Paper p="lg" withBorder>
        <Stack gap="md">
          <Title order={4}>Output Handles</Title>
          <OutputHandleEditor handles={values.outputHandles} onChange={(outputHandles) => update({ outputHandles })} />
        </Stack>
      </Paper>

      <Group justify="flex-end">
        <Button variant="subtle" onClick={goBack}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!values.name.trim() || !values.personaName.trim()}
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}
